import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
  type SyntheticEvent,
} from 'react'
import Snackbar from '@mui/material/Snackbar'
import type { SnackbarCloseReason } from '@mui/material/Snackbar'
import { AppToast } from './AppToast'
import {
  APP_TOAST_AUTO_HIDE_MS,
  APP_TOAST_SNACKBAR_ANCHOR,
  APP_TOAST_SNACKBAR_SX,
  shouldDismissAppToastOnSnackbarClose,
} from './toastPlacement'

export type ToastSeverity = 'success' | 'error' | 'warning' | 'info'

interface ToastContextValue {
  showToast: (message: string, severity?: ToastSeverity, autoHideDuration?: number) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

export function ToastProvider({ children }: { readonly children: ReactNode }) {
  const [open, setOpen] = useState(false)
  const [message, setMessage] = useState('')
  const [severity, setSeverity] = useState<ToastSeverity>('info')
  const [toastSession, setToastSession] = useState(0)
  const [autoHideDuration, setAutoHideDuration] = useState(APP_TOAST_AUTO_HIDE_MS)

  const showToast = useCallback(
    (msg: string, sev: ToastSeverity = 'info', hideMs?: number) => {
      setMessage(msg)
      setSeverity(sev)
      setAutoHideDuration(hideMs ?? APP_TOAST_AUTO_HIDE_MS)
      setToastSession((n) => n + 1)
      setOpen(true)
    },
    [],
  )

  const value = useMemo(() => ({ showToast }), [showToast])

  const handleDismiss = useCallback(() => {
    setOpen(false)
  }, [])

  const handleSnackbarClose = useCallback(
    (_event: Event | SyntheticEvent, reason: SnackbarCloseReason) => {
      if (shouldDismissAppToastOnSnackbarClose(reason)) {
        setOpen(false)
      }
    },
    [],
  )

  return (
    <ToastContext.Provider value={value}>
      {children}
      <Snackbar
        open={open}
        autoHideDuration={autoHideDuration}
        onClose={handleSnackbarClose}
        anchorOrigin={APP_TOAST_SNACKBAR_ANCHOR}
        sx={APP_TOAST_SNACKBAR_SX}
        data-testid="app-toast-snackbar"
      >
        <AppToast
          key={toastSession}
          message={message}
          severity={severity}
          onClose={handleDismiss}
        />
      </Snackbar>
    </ToastContext.Provider>
  )
}

export function useToast() {
  const ctx = useContext(ToastContext)
  if (!ctx) {
    throw new Error('useToast must be used within ToastProvider')
  }
  return ctx
}

/** Alias for feature code that follows Sensemaker naming (`useNotify`). */
export function useNotify() {
  return useToast()
}
